import { useEffect, useRef } from "react";
import { useDebounce } from "use-debounce";

import { useAuthStore } from "@/features/auth";
import { useSocket } from "@/lib/socket";
import { useCompanyStore } from "@/features/company";
import { useOrganizationStore } from "@/features/organization";
import { useRolesPermissionsStore } from "./useRolesPermissionsStore";
import { useUserStore } from "./useUserStore";

export const useUserListPageEffects = () => {
  const { user, accessToken } = useAuthStore();
  const currentUserId = user?.userId || user?.id;
  const companyId = user?.companyId;
  const socket = useSocket(currentUserId, accessToken);

  const {
    roles,
    permissionsModules,
    loadingModules,
    loadingRoles,
    fetchRoleList,
    fetchPermissionModules,
  } = useRolesPermissionsStore();

  const { fetchPublicCompanies, companiesLoaded, companyOptions } =
    useCompanyStore();
  const { fetchHierarchy } = useOrganizationStore();
  const orgLoadedRef = useRef(false);
  const {
    page,
    pageSize,
    filters,
    sort,
    checkCanCreateUser,
    updateOnlineUserStatus,
    loadUsers,
  } = useUserStore();

  const permissions = user?.permissions || [];

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  const canViewRoles = hasPermission("user_list:view");
  const canViewUserList = hasPermission("user_list:view");
  const canCheckCreateUser = hasPermission("user_list:view");

  const [debouncedUsername] = useDebounce(filters.username, 400);
  const [debouncedEmail] = useDebounce(filters.email, 400);
  const [debouncedCompany] = useDebounce(filters.company, 400);
  const [debouncedOrganization] = useDebounce(filters.org, 400);
  const [debouncedRole] = useDebounce(filters.role, 400);

  const rolesFetchedRef = useRef(false);
  const modulesFetchedRef = useRef(false);

  useEffect(() => {
    if (!canCheckCreateUser) return;
    if (!companyId) return;
    checkCanCreateUser(companyId);
  }, [checkCanCreateUser, companyId]);

  useEffect(() => {
    if (!canViewRoles) return;
    if (!roles.length && !loadingRoles) {
      fetchRoleList();
    }
  }, [fetchRoleList, loadingRoles, roles.length]);

  useEffect(() => {
    if (!permissionsModules.length && !loadingModules && canViewRoles) {
      fetchPermissionModules({ audience: "all" });
    }
  }, [fetchPermissionModules, loadingModules, permissionsModules.length]);

  useEffect(() => {
    if (orgLoadedRef.current) return;
    orgLoadedRef.current = true;
    fetchHierarchy().catch(() => {});
  }, [fetchHierarchy]);

  useEffect(() => {
    if (companiesLoaded || companyOptions.length > 0) return;
    fetchPublicCompanies().catch(() => {});
  }, [companiesLoaded, companyOptions.length, fetchPublicCompanies]);

  useEffect(() => {
    if (!socket?.on) return;

    const handleOnlineUserStatus = ({ userId, status, lastActive }) => {
      updateOnlineUserStatus(userId, status, lastActive);
    };

    socket.on("user-status", handleOnlineUserStatus);

    return () => {
      socket.off?.("user-status", handleOnlineUserStatus);
    };
  }, [socket, updateOnlineUserStatus]);

  useEffect(() => {
    if (!canViewUserList) return;
    if (!user) return;
    loadUsers();
  }, [
    user,
    debouncedUsername,
    debouncedEmail,
    debouncedCompany,
    debouncedOrganization,
    debouncedRole,
    sort.key,
    sort.direction,
    page,
    pageSize,
    loadUsers,
  ]);

  return null;
};
